import { AdditionDto, CaseCalculationPart } from '../interfaces/Case';
import { IReferential } from './IReferential';
import {
  CalculationSectionCodeEnum,
  CalculationTypeCode,
  CurrencyCode
} from '../helpers/Constants';
import { Utils } from '../helpers/Utils';

/**
 * Pricing flow:
 * Gross -> Discounted -> Insurer Added -> GTM Added
 *
 * Unit states:
 * U0 = P
 * U1 = U0 * (1 - d)
 * U2 = U1 * (1 + a)
 * U3 = U2 * (1 + g)
 *
 * Line states:
 * L0 = n * U0
 * L1 = n * U1
 * L2 = n * U2
 * L3 = n * U3
 *
 * Components:
 * Discount         = L0 - L1
 * InsurerAddition  = L2 - L1
 * GTMAddition      = L3 - L2
 *
 * U0 = P
 */
export class CalculationParts {
  public Parts: CaseCalculationPart[];
  private _sections: IReferential[];
  private _addition: AdditionDto;

  private _vat: number;
  private _smallPartPercentage: number;
  private _calculationType: string;
  private _ignoreAlternativePart: boolean;

  public CurrencyCode = '';

  public constructor(
    sections: IReferential[],
    parts: CaseCalculationPart[],
    vat: number,
    smallPartsPercent: number,
    additionDto: AdditionDto,
    calculationType: string,
    ignoreAlternativePart = false
  ) {
    this._sections = sections;
    this.Parts = parts;

    this._vat = vat / 100;
    this._smallPartPercentage = smallPartsPercent;

    this._addition = additionDto;
    this._calculationType = calculationType;
    this._ignoreAlternativePart = ignoreAlternativePart;
  }

  public get AdditionDto(): AdditionDto {
    return this._addition;
  }

  public get SmallPartPercentage(): number {
    return this._smallPartPercentage;
  }

  private Round(value: number, decimals = 2): number {
    return Utils.Round(value, decimals);
  }

  /** U0 = P */
  public GetBaseUnitPrice(item: CaseCalculationPart): number {
    return this.GetPrice(item);
  }

  /** U1 = U0 * (1 - d) */
  public GetUnitPriceWithDiscount(item: CaseCalculationPart): number {
    return this.Round(this.GetBaseUnitPrice(item) * (1 - this.GetDiscount(item)));
  }

  /** U2 = U1 * (1 + a) */
  public GetUnitPriceWithInsurerAddition(item: CaseCalculationPart): number {
    return this.Round(this.GetUnitPriceWithDiscount(item) * (1 + this.GetInsurerAddition(item)));
  }

  /**
   * U3 = Round(U2 * (1 + g), 2) + AdditionFixedValue   daca includeGTM = true
   * altfel U3 = U2
   */
  public GetUnitPrice(item: CaseCalculationPart, includeGTM = false): number {
    let unitPrice = this.GetUnitPriceWithInsurerAddition(item);

    if (includeGTM) {
      unitPrice = this.Round(
        this.Round(unitPrice * (1 + this.GetGTMAddition(item))) + (item.additionFixedValue ?? 0)
      );
    }

    return unitPrice;
  }

  /** L0 = n * U0 */
  private GetGrossLine(item: CaseCalculationPart): number {
    return this.Round(item.pieceNr * this.GetBaseUnitPrice(item));
  }

  /** L1 = n * U1 */
  private GetLineAfterDiscount(item: CaseCalculationPart): number {
    return this.Round(item.pieceNr * this.GetUnitPriceWithDiscount(item));
  }

  /** L2 = n * U2 */
  private GetLineWithInsurerAddition(item: CaseCalculationPart): number {
    return this.Round(item.pieceNr * this.GetUnitPriceWithInsurerAddition(item));
  }

  /**
   * L3 = Round(n * U3, 2)
   * U3 include deja AdditionFixedValue (aliniat cu GetCalculationSectionParts SP).
   */
  public GetTotalPrice(item: CaseCalculationPart, includeGTM = false): number {
    return this.Round(item.pieceNr * this.GetUnitPrice(item, includeGTM));
  }

  /** Discount = L0 - L1 */
  public GetLineDiscount(item: CaseCalculationPart): number {
    return this.GetGrossLine(item) - this.GetLineAfterDiscount(item);
  }

  /** InsurerAddition = L2 - L1 */
  public GetLineInsurerAddition(item: CaseCalculationPart): number {
    return this.GetLineWithInsurerAddition(item) - this.GetLineAfterDiscount(item);
  }

  /** GTMAddition = L3 - L2 */
  public GetLineGTMAddition(item: CaseCalculationPart): number {
    return this.GetTotalPrice(item, true) - this.GetLineWithInsurerAddition(item);
  }

  private GetSectionId(section: string): number {
    return this._sections.find((item) => item.isActive && item.code === section)!.id;
  }

  public GetSmallPartsValue(): number {
    const amount =
      this._calculationType === CalculationTypeCode.ORG
        ? this.GetAmountPart(CalculationSectionCodeEnum.PART)
        : this.GetAmountPartWithDiscount(CalculationSectionCodeEnum.PART, false);

    return this.Round((amount * this._smallPartPercentage) / 100);
  }

  private GetPrice(part: CaseCalculationPart): number {
    return part.alternativePartQualityId === null || this._ignoreAlternativePart
      ? part.originalPrice
      : part.alternativePrice ?? 0;
  }

  public GetDiscount(part: CaseCalculationPart): number {
    const discount =
      part.alternativePartQualityId === null || this._ignoreAlternativePart
        ? part.discount
        : part.alternativePartDiscount;

    return discount !== null && discount !== undefined ? discount / 100 : 0;
  }

  private GetInsurerAddition(part: CaseCalculationPart): number {
    return part.additionInsurer !== null ? part.additionInsurer / 100 : 0;
  }

  private GetGTMAddition(part: CaseCalculationPart): number {
    return part.additionValue / 100;
  }

  public GetAmountPart(section: string): number {
    const partsSectionId = this.GetSectionId(section);
    return this.Parts.filter((item) => item.calculationSectionId === partsSectionId).reduce(
      (sum, item) => sum + this.GetGrossLine(item),
      0
    );
  }

  public GetAmountPartWithDiscount(section: string, includeSmallParts = true): number {
    const partsSectionId = this.GetSectionId(section);
    const smallPartValue =
      includeSmallParts && section === CalculationSectionCodeEnum.PART
        ? this.GetSmallPartsValue()
        : 0;

    return (
      smallPartValue +
      this.Parts.filter((item) => item.calculationSectionId === partsSectionId).reduce(
        (sum, item) => sum + this.GetTotalPrice(item),
        0
      )
    );
  }

  public ApplyGtmPercent(): boolean {
    return this.TotalWithoutVatValue() > this._addition.marginValue;
  }

  public TotalPercentAddition(): number {
    return this.Parts.reduce((sum, item) => sum + this.GetLineGTMAddition(item), 0);
  }

  public TotalAddition(includePercent = true): number {
    if (this.ApplyGtmPercent()) {
      return includePercent ? this.TotalPercentAddition() : 0;
    }
    return this._addition.fixedValue;
  }

  public TotalInsurerAddition(section: string): number {
    const partsSectionId = this.GetSectionId(section);
    return this.Parts.filter((item) => item.calculationSectionId === partsSectionId).reduce(
      (sum, item) => sum + this.GetLineInsurerAddition(item),
      0
    );
  }

  public TotalPartsDiscount(section: string): number {
    const partsSectionId = this.GetSectionId(section);
    return this.Parts.filter((item) => item.calculationSectionId === partsSectionId).reduce(
      (sum, item) => sum + this.GetLineDiscount(item),
      0
    );
  }

  public TotalGTMAddition(section: string): number {
    const partsSectionId = this.GetSectionId(section);
    return this.Parts.filter((item) => item.calculationSectionId === partsSectionId).reduce(
      (sum, item) => sum + this.GetLineGTMAddition(item),
      0
    );
  }

  public TotalWithoutVatValue(includeGTM = false): number {
    return (
      this.GetSmallPartsValue() +
      this.Parts.reduce((sum, item) => sum + this.GetTotalPrice(item, includeGTM), 0)
    );
  }

  public TotalWithoutVAT(): number {
    const amountWithoutTVA = this.TotalWithoutVatValue(true);

    if (this.CurrencyCode === CurrencyCode.RON) {
      return amountWithoutTVA;
    }

    const totalAddition = this.TotalAddition(false);
    return amountWithoutTVA + totalAddition;
  }

  public TotalVAT(): number {
    return this.Round(this.TotalWithoutVAT() * this._vat);
  }

  public Total(): number {
    return this.TotalWithoutVAT() + this.TotalVAT();
  }

  /**
   * Distribuie un adaos fix proportional pe L2 (pretul dupa insurer addition) al fiecarei piese.
   * Se aplica DOAR pentru RON. Ultima piesa primeste remainder-ul pentru Σ = fixedAmount exact.
   */
  public DistributeFixedAddition(fixedAmount: number): void {
    if (fixedAmount === 0 || this.Parts.length === 0 || this.CurrencyCode !== CurrencyCode.RON) {
      return;
    }

    const partsSectionId = this.GetSectionId(CalculationSectionCodeEnum.PART);
    const parts = this.Parts.filter((item) => item.calculationSectionId === partsSectionId);

    if (parts.length === 0) {
      return;
    }

    // Baza de distributie: L2 per piesa (suma dupa insurer addition)
    const totalL2 = parts.reduce((sum, item) => sum + this.GetLineWithInsurerAddition(item), 0);
    if (totalL2 === 0) {
      return;
    }

    // Distribuie proportional pe primele N-1 piese
    let allocated = 0;
    for (let i = 0; i < parts.length - 1; i++) {
      const item = parts[i];
      const share = this.Round((fixedAmount * this.GetLineWithInsurerAddition(item)) / totalL2);
      item.additionFixedValue = item.pieceNr !== 0 ? share / item.pieceNr : 0;
      allocated += share;
    }

    // Ultima piesa primeste restul exact — garanteaza Σ = fixedAmount fara discrepante
    const last = parts[parts.length - 1];
    if (last !== undefined) {
      last.additionFixedValue = last.pieceNr !== 0 ? (fixedAmount - allocated) / last.pieceNr : 0;
    }
  }
}
